import React, { useState, useMemo } from "react";
import { GetAll, Delete } from "adminApi/ParkingBookingApi/ParkingBooking";
import { useRoleVerify } from "Common/RoleVerify";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/BookingFeilds.json";

const bookingStatusMap = {
  pending: "warning",
  confirmed: "success",
  cancelled: "danger",
  completed: "info",
};

const paymentStatusMap = {
  unpaid: "danger",
  paid: "success",
  partial: "warning",
  refunded: "secondary",
};

const BookingListTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
  const sizeOptions = [10, 20, 50, 100];
  const { tabledata } = fields;
  // Fetch Hook
  const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAll, {
    page,
    limit,
    search: debouncedSearch,
    title: "Booking List",
  });

  const displayFields = generateDisplayFields(tabledata);

  // Format status columns as colored badges, show dash for empty parking_note
  const formattedData = useMemo(() => data.map(item => ({
    ...item,
    parking_note: item.parking_note || "—",
    booking_status: item.booking_status ? (
      <span className={`badge bg-${bookingStatusMap[item.booking_status] || "secondary"}`}>
        {item.booking_status}
      </span>
    ) : <span className="badge bg-secondary">pending</span>,
    payment_status: item.payment_status ? (
      <span className={`badge bg-${paymentStatusMap[item.payment_status] || "secondary"}`}>
        {item.payment_status}
      </span>
    ) : <span className="badge bg-danger">unpaid</span>,
  })), [data]);

  // Table Hook
  const { formattedData: tableData, columns } = useTableConfig({
    data: formattedData,
    page,
    limit,
    canShowAction,
    refetch,
    IsDelete: true,
    IsEditDisble: true,
    IsEditDisbleKey: "payment_key",
    config: {
      entityName: "Booking",
      editUrl: "edit-booking",
      idKey: "parking_booking_id",
      deleteApi: Delete,
      displayFields,
    },
  });

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const onPageChange = (p) => setPage(p);
  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={tableData}
      options={{
        loading,
        error,
        sortableColumns: displayFields.map((f) => f.accessor),
        copy: true,
        csv: true,
        pdf: true,
        print: true,
        fileName: "Parking Booking",
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch: handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default BookingListTable;